/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Operations › PC switches (owner, 2026-10-21; approved as drawn in CommandCenter at 17:33 PT, with the PCs table showing
 * "computer name and UPN of user"). Every switch each PC used to get only from its Intune-written settings, set here once
 * for everyone, with exceptions for people; PCs follow within minutes and report what they run with. Three views, each its
 * own `?view=` like Support's: For everyone, Exceptions for people and PCs. One save bar covers the two that edit, and
 * saves the whole policy with the revision it was loaded at: when another admin saved first the service answers 409 and
 * the person's edits are kept, to lay over the new revision after a Refresh. Auditors see the page read-only.
 *
 * The words are the approved mockup's (lib/pcSwitches.ts SWITCH_WORDS); the switches and their types are the service's
 * catalog (GET /v1/admin/desktop/policy `catalog`). Drawn from the kit; the few pieces of its own are kit.css `pcsw-*`.
 */
import { Fragment, useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import {
  Bot, Building2, Check, CircleDashed, Cloud, FileCog, FolderOpen, Info, Lock, Minus, Monitor, Package, Pause, Plug, Plus, Power,
  RefreshCw, Search, Sparkle, Trash2, UserCog, UserPlus, Users, X, type LucideIcon,
} from 'lucide-react';
import { api } from '../lib/api';
import { EarlyCircleSection } from '../components/EarlyCircleSection';
import { PeoplePicker } from '../components/PeoplePicker';
import { SaveNote } from '../components/SaveNote';
import { peopleIn } from '../lib/people';
import {
  NO_EDITS, CONFLICT_TEXT,
  agoText, allowListRefusal, catalogOf, changesOf, chipValue, choiceWords, circleText, differenceChips, draftOf, everyoneElseText, exceptionDefault,
  exceptionSummary, filterPcs, fleetChanged, followsOnlyItSetting, isStale, namesText, offeredChoices, oidForEmail, orderedPcs, pageOf,
  panelsFor, pcName, pcsDescription, peopleRows, peopleText, personChanged, personCount, personSub, personTitle, resolveFailureText, revisionLine,
  revisionText, saveBody, saveFailureText, savedText, shownOn, storedFor, valueText, viewFrom, withFleetEdit, withoutPerson, withPersonEdit,
  wordsFor,
  type CatalogEntry, type DesktopPolicyView, type DevicePolicyRow, type DifferenceChip, type PanelView, type PcSwitchesView, type PolicyDraft,
  type PolicyEdits, type PolicyPerson, type SwitchSource, type SwitchValue,
} from '../lib/pcSwitches';
import { linkClick, usePlace } from '../shell/router';
import {
  Button, Callout, EmptyState, ErrorState, Field, Grid, Icon, Input, PageHeader, Panel, SaveBar, Segmented, Select, Skeleton, Stack, StatusChip,
  Table, Tag, type Column, type Tone,
} from '../ui';
import { dateTime } from '../ui/format';

const PANEL_ICONS: Record<string, LucideIcon> = {
  gyro: Power,
  autonomy: Bot,
  connections: Plug,
  revit: Building2,
  claude: Sparkle,
  files: FolderOpen,
  admin: UserCog,
  updates: Package,
};

const SOURCE_LOOK: Record<SwitchSource | 'unknown', { tone: Tone; icon: LucideIcon }> = {
  server: { tone: 'info', icon: Cloud },
  machine: { tone: 'neutral', icon: FileCog },
  default: { tone: 'neutral', icon: CircleDashed },
  quarantine: { tone: 'bad', icon: Lock },
  'user-pause': { tone: 'neutral', icon: Pause },
  unknown: { tone: 'neutral', icon: Minus },
};

/** A choice with this many values or fewer is a segmented control (Canary / Early / Broad); more is a drop-down. */
const SEGMENTED_CHOICES = 3;

const READ_ONLY = (
  <Callout tone="neutral" title="Read only">Only an IT admin can change the PC switches and the exceptions for people.</Callout>
);

/** A switch's control, for everyone (with Not set) or in an exception (a value only). */
function ValueControl({ entry, value, onChange, label, allowUnset, disabled, changed }: {
  entry: CatalogEntry;
  value: SwitchValue | undefined;
  onChange: (value: SwitchValue | undefined) => void;
  label: string;
  allowUnset: boolean;
  disabled: boolean;
  changed: boolean;
}) {
  const words = wordsFor(entry);
  const className = changed ? 'is-changed' : undefined;
  if (entry.type === 'boolean') {
    const current = value === undefined ? 'unset' : shownOn(words, value as boolean) ? 'on' : 'off';
    const options = [{ value: 'on', label: 'On' }, { value: 'off', label: 'Off' }, ...(allowUnset ? [{ value: 'unset', label: 'Not set' }] : [])];
    return (
      <Segmented
        size="md"
        label={label}
        options={options}
        value={current}
        disabled={disabled}
        className={className}
        onChange={(next) => onChange(next === 'unset' ? undefined : storedFor(words, next === 'on'))}
      />
    );
  }
  if (entry.type === 'list') {
    if (!allowUnset) return null;
    return (
      <Segmented
        size="md"
        label={label}
        options={[{ value: 'list', label: 'Use this list' }, { value: 'unset', label: 'Not set' }]}
        value={value === undefined ? 'unset' : 'list'}
        disabled={disabled}
        className={className}
        onChange={(next) => onChange(next === 'unset' ? undefined : Array.isArray(value) ? value : [])}
      />
    );
  }
  const choices = offeredChoices(entry, words, [value]);
  if ((entry.choices?.length ?? 0) <= SEGMENTED_CHOICES) {
    const options = [...choices.map((choice) => ({ value: choice, label: choiceWords(words, choice).option })), ...(allowUnset ? [{ value: '', label: 'Not set' }] : [])];
    return (
      <Segmented
        size="md"
        label={label}
        options={options}
        value={typeof value === 'string' ? value : ''}
        disabled={disabled}
        className={className}
        onChange={(next) => onChange(next === '' ? undefined : next)}
      />
    );
  }
  return (
    <Select aria-label={label} value={typeof value === 'string' ? value : ''} disabled={disabled} changed={changed} onChange={(event) => onChange(event.target.value === '' ? undefined : event.target.value)}>
      {allowUnset ? <option value="">Not set (each PC keeps its IT setting)</option> : null}
      {choices.map((choice) => <option key={choice} value={choice}>{choiceWords(words, choice).option}</option>)}
    </Select>
  );
}

/** The allow list's names as chips, and the box that adds one (the service's rules checked first). */
function AllowListEditor({ names, savedNames, limits, canEdit, label, onChange }: {
  names: readonly string[] | undefined;
  /** The saved list: a name not on it has the unsaved amber edge. */
  savedNames: readonly string[] | undefined;
  limits?: { entries: number; chars: number };
  canEdit: boolean;
  label: string;
  onChange: (names: string[]) => void;
}) {
  const [typed, setTyped] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const list = names ?? [];
  const saved = new Set((savedNames ?? []).map((name) => name.toLowerCase()));
  const add = () => {
    const refusal = allowListRefusal(list, typed, limits);
    if (refusal) {
      setNote(refusal);
      return;
    }
    onChange([...list, typed.trim()]);
    setTyped('');
    setNote(null);
  };
  return (
    <div className="pcsw-list">
      {list.length > 0 ? (
        <ul className="people-chips" aria-label={`${label}: ${namesText(list.length)}`}>
          {list.map((name) => (
            <li key={name.toLowerCase()} className={saved.has(name.toLowerCase()) || !savedNames ? 'people-chip' : 'people-chip is-changed'}>
              <span>{name}</span>
              {canEdit ? (
                <button type="button" aria-label={`Remove ${name}`} onClick={() => { onChange(list.filter((entry) => entry !== name)); setNote(null); }}>
                  <Icon icon={X} size="xs" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : names ? <span className="ui-cell-sub">The list is empty: no connection is allowed by name alone.</span> : null}
      {canEdit ? (
        <div className="ui-actions">
          <Input
            className="ui-grow"
            aria-label={`Connection to add to the ${label.toLowerCase()}`}
            placeholder="A connection's name, exactly as it is set up on the PC"
            autoComplete="off"
            spellCheck={false}
            value={typed}
            onChange={(event) => { setTyped(event.target.value); setNote(null); }}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return;
              event.preventDefault();
              add();
            }}
          />
          <Button icon={Plus} disabled={!typed.trim()} onClick={add}>Add</Button>
        </div>
      ) : null}
      {note ? <p className="ui-field-note is-error" role="alert">{note}</p> : null}
    </div>
  );
}

/** One switch for everyone: its words, who gets it differently, and its control with the line under it. */
function SwitchRow({ entry, saved, draft, edits, canEdit, exceptionsHref, onEdit }: {
  entry: CatalogEntry;
  saved: DesktopPolicyView;
  draft: PolicyDraft;
  edits: PolicyEdits;
  canEdit: boolean;
  exceptionsHref: string;
  onEdit: (edits: PolicyEdits) => void;
}) {
  const words = wordsFor(entry);
  const value = draft.fleet[entry.key];
  const changed = fleetChanged(saved, edits, entry.key);
  const summary = exceptionSummary(entry, draft);
  const set = (next: SwitchValue | undefined) => onEdit(withFleetEdit(edits, entry.key, next === undefined ? null : next));
  return (
    <tr>
      <td>
        <div className="ui-cell-stack">
          <span className="ui-cell-title"><strong>{words.label}</strong></span>
          {words.description ? <span className="ui-cell-sub pcsw-desc">{words.description}</span> : null}
          {summary.length > 0 ? (
            <span className="ui-cell-sub">
              {summary.map((part, index) => (
                <Fragment key={part.lead}>
                  {index > 0 ? ' · ' : null}
                  {part.lead} <a href={exceptionsHref} onClick={(event) => linkClick(event)}>{personCount(part.count)}</a>
                </Fragment>
              ))}
            </span>
          ) : null}
          {entry.type === 'list' ? (
            <AllowListEditor
              names={Array.isArray(value) ? value : undefined}
              savedNames={Array.isArray(saved.fleet[entry.key]) ? saved.fleet[entry.key] as string[] : undefined}
              limits={entry.limits}
              canEdit={canEdit}
              label={words.label}
              onChange={(names) => set(names)}
            />
          ) : null}
        </div>
      </td>
      <td className="is-end pcsw-control-cell" data-th="For everyone">
        <div className="pcsw-control">
          <ValueControl entry={entry} value={value} onChange={set} label={`${words.label}, for everyone`} allowUnset disabled={!canEdit} changed={changed} />
          {changed ? <p className="ui-field-note is-changed">Unsaved · was {valueText(entry, saved.fleet[entry.key])}</p>
            : value === undefined ? <p className="ui-field-note">Each PC keeps its IT setting</p> : null}
        </div>
      </td>
    </tr>
  );
}

function SwitchPanel({ panel, ...row }: { panel: PanelView } & Omit<Parameters<typeof SwitchRow>[0], 'entry'>) {
  return (
    <Panel
      flush
      title={<span className="ui-title-icon"><Icon icon={PANEL_ICONS[panel.id] ?? Monitor} size="md" />{panel.title}</span>}
      description={panel.description || undefined}
      actions={panel.viaUpdater ? <StatusChip tone="info" icon={Info}>Applies once the new updater is installed</StatusChip> : undefined}
    >
      <table className="ui-table ui-table--tall is-stacked pcsw-switches" aria-label={panel.title}>
        <tbody>
          {panel.entries.map((entry) => <SwitchRow key={entry.key} entry={entry} {...row} />)}
        </tbody>
      </table>
    </Panel>
  );
}

/** Add an exception: a person from the people picker, a switch, and what they get. */
function AddException({ catalog, panels, draft, edits, onEdit }: {
  catalog: readonly CatalogEntry[];
  panels: readonly PanelView[];
  draft: PolicyDraft;
  edits: PolicyEdits;
  onEdit: (edits: PolicyEdits) => void;
}) {
  const id = useId();
  const [person, setPerson] = useState('');
  const [key, setKey] = useState(() => panels[0]?.entries[0]?.key ?? catalog[0]?.key ?? '');
  const [picked, setPicked] = useState<{ key: string; value: SwitchValue } | null>(null);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [looking, setLooking] = useState(false);
  const entry = catalog.find((candidate) => candidate.key === key) ?? catalog[0];
  if (!entry) return null;
  const words = wordsFor(entry);
  const value = picked && picked.key === entry.key ? picked.value : exceptionDefault(entry, draft.fleet);

  const add = async () => {
    const email = peopleIn(person)[0]?.toLowerCase();
    if (!email) {
      setNote({ ok: false, text: 'Choose a person first.' });
      return;
    }
    // Someone already on the policy keeps their object id; anyone else is resolved by the service from desktop
    // enrollment (the people picker answers e-mails; exceptions are keyed by object id). No PC yet: nothing is added.
    let oid = oidForEmail(email, draft.people);
    let upn = email;
    if (!oid) {
      setLooking(true);
      try {
        const found = await api.resolveDesktopPolicyPerson(email);
        oid = found.oid.toLowerCase();
        upn = found.upn || email;
      } catch (error) {
        setNote({ ok: false, text: resolveFailureText(error, email) });
        return;
      } finally {
        setLooking(false);
      }
    }
    onEdit(withPersonEdit(edits, oid, entry.key, value, upn));
    setPerson('');
    setPicked(null);
    setNote(null);
  };

  const theyGet = (
    <ValueControl entry={entry} value={value} onChange={(next) => next !== undefined && setPicked({ key: entry.key, value: next })} label="They get" allowUnset={false} disabled={false} changed={false} />
  );
  return (
    <Panel
      title={<span className="ui-title-icon"><Icon icon={UserPlus} size="md" />Add an exception</span>}
      description="A person gets a switch differently from everyone, on every PC they use"
    >
      <div className="ui-form">
        <div className="ui-field">
          <label className="ui-field-label" htmlFor={`${id}-person`}>Person</label>
          <PeoplePicker id={`${id}-person`} label="Person" value={person} max={1} onChange={(next) => { setPerson(next); setNote(null); }} />
          <p className="ui-field-note">Someone who has signed in to Gyro on their desktop.</p>
        </div>
        <Grid cols={2}>
          <Field label="Switch">
            <Select value={entry.key} onChange={(event) => { setKey(event.target.value); setPicked(null); setNote(null); }}>
              {panels.map((panel) => (
                <optgroup key={panel.id} label={panel.title}>
                  {panel.entries.map((candidate) => <option key={candidate.key} value={candidate.key}>{wordsFor(candidate).label}</option>)}
                </optgroup>
              ))}
            </Select>
          </Field>
          {entry.type === 'list' ? (
            <div className="ui-field">
              <span className="ui-field-label">They get</span>
              <AllowListEditor names={value as string[]} savedNames={undefined} limits={entry.limits} canEdit label={words.label} onChange={(names) => setPicked({ key: entry.key, value: names })} />
              <p className="ui-field-note">{everyoneElseText(entry, draft.fleet)}</p>
            </div>
          ) : entry.type === 'choice' && (entry.choices?.length ?? 0) > SEGMENTED_CHOICES ? (
            <Field label="They get" help={everyoneElseText(entry, draft.fleet)}>{theyGet}</Field>
          ) : (
            <div className="ui-field">
              <span className="ui-field-label">They get</span>
              <div>{theyGet}</div>
              <p className="ui-field-note">{everyoneElseText(entry, draft.fleet)}</p>
            </div>
          )}
        </Grid>
        <div className="ui-actions">
          <Button icon={Plus} busy={looking} onClick={() => void add()}>Add exception</Button>
        </div>
        <SaveNote note={note} />
      </div>
    </Panel>
  );
}

/** The person cell: the name over the sign-in e-mail when the service knows a name, else the e-mail, else "Unknown person". */
function PersonCell({ person, oid }: { person: { name?: string; upn?: string } | null; oid?: string }) {
  const sub = personSub(person);
  const known = Boolean(person?.name?.trim() || person?.upn?.trim());
  return (
    <div className="ui-cell-stack">
      <span>{personTitle(person)}</span>
      {sub ? <span className="ui-cell-sub">{sub}</span> : null}
      {!known && oid ? <span className="ui-cell-sub" title={oid}>Object id {oid.slice(0, 8)}…</span> : null}
    </div>
  );
}

function ExceptionsView({ saved, draft, edits, catalog, panels, canEdit, onEdit, notice }: {
  /** A note above the panels (a refresh that failed). */
  notice?: ReactNode;
  saved: DesktopPolicyView;
  draft: PolicyDraft;
  edits: PolicyEdits;
  catalog: readonly CatalogEntry[];
  panels: readonly PanelView[];
  canEdit: boolean;
  onEdit: (edits: PolicyEdits) => void;
}) {
  const rows = peopleRows(draft);
  const order = new Map(catalog.map((entry, index) => [entry.key, index]));
  const columns: Column<{ oid: string; person: PolicyPerson }>[] = [
    { key: 'person', header: 'Person', width: '30%', cell: (row) => <PersonCell person={row.person} oid={row.oid} /> },
    {
      key: 'differently',
      header: 'Gets differently',
      cell: (row) => {
        const who = personTitle(row.person);
        const keys = Object.keys(row.person.switches).sort((a, b) => (order.get(a) ?? Number.MAX_SAFE_INTEGER) - (order.get(b) ?? Number.MAX_SAFE_INTEGER));
        return (
          <ul className="people-chips" aria-label={`${who} gets differently`}>
            {keys.map((key) => {
              const entry = catalog.find((candidate) => candidate.key === key) ?? { key, label: key, group: '', type: 'boolean' as const, description: '', appliesVia: 'agent' as const };
              const short = wordsFor(entry).short;
              return (
                <li key={key} className={personChanged(saved, edits, row.oid, key) ? 'people-chip is-changed' : 'people-chip'}>
                  <span>{short}: <span className="pcsw-chip-value">{chipValue(entry, row.person.switches[key])}</span></span>
                  {canEdit ? (
                    <button type="button" aria-label={`Remove ${short} for ${who}`} onClick={() => onEdit(withPersonEdit(edits, row.oid, key, null))}>
                      <Icon icon={X} size="xs" />
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        );
      },
    },
    ...(canEdit ? [{
      key: 'actions',
      header: <span className="ui-sr">Actions</span>,
      align: 'end' as const,
      cell: (row: { oid: string; person: PolicyPerson }) => (
        <span className="ui-row-actions">
          <Button variant="danger" size="sm" icon={Trash2} aria-label={`Remove all of ${personTitle(row.person)}'s exceptions`} onClick={() => onEdit(withoutPerson(edits, draft, row.oid))}>
            Remove
          </Button>
        </span>
      ),
    }] : []),
  ];
  return (
    <Stack>
      {notice}
      {canEdit ? <AddException catalog={catalog} panels={panels} draft={draft} edits={edits} onEdit={onEdit} /> : READ_ONLY}
      <Panel
        flush
        title={<span className="ui-title-icon"><Icon icon={Users} size="md" />People with exceptions</span>}
        description={rows.length > 0 ? peopleText(rows.length) : 'No one gets a switch differently from everyone'}
        footer={<><Icon icon={Info} size="sm" /><span>An exception wins over For everyone for that person, on every PC they use. Remove it and they follow For everyone again.</span></>}
      >
        <Table
          stacked
          stickyHeader={false}
          className="ui-table--tall"
          label="People with exceptions"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.oid}
          empty={(
            <EmptyState compact icon={Users} title="No exceptions yet">
              Everyone gets the switches set under For everyone.{canEdit ? ' Add an exception above to give one person a switch differently.' : ''}
            </EmptyState>
          )}
        />
      </Panel>
    </Stack>
  );
}

function DifferenceChipView({ chip }: { chip: DifferenceChip }) {
  const look = SOURCE_LOOK[chip.source ?? 'unknown'];
  return (
    <StatusChip tone={look.tone} icon={look.icon} title={chip.title}>
      <span>{chip.label}: <span className="pcsw-chip-value">{chip.value}</span></span>
    </StatusChip>
  );
}

function PcsView({ saved, devices, devicesProblem, catalog, now, onRetry, notice }: {
  /** A note above the panel (a refresh that failed). */
  notice?: ReactNode;
  saved: DesktopPolicyView;
  devices: readonly DevicePolicyRow[] | null;
  devicesProblem: unknown;
  catalog: readonly CatalogEntry[];
  now: number;
  onRetry: () => void;
}) {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const ordered = useMemo(() => orderedPcs(devices ?? []), [devices]);
  const filtered = useMemo(() => filterPcs(ordered, query), [ordered, query]);
  const shown = pageOf(filtered, page);
  const columns: Column<DevicePolicyRow>[] = [
    { key: 'person', header: 'Person', width: '20%', cell: (row) => <PersonCell person={row.person} oid={row.person?.oid} /> },
    {
      key: 'pc',
      header: 'PC',
      width: '128px',
      cell: (row) => {
        const pc = pcName(row);
        return <Tag title={row.deviceId}>{pc.text}</Tag>;
      },
    },
    {
      key: 'version',
      header: 'Gyro version',
      width: '180px',
      cell: (row) => followsOnlyItSetting(row, saved.revision) ? (
        <div className="ui-cell-stack">
          <span className="is-num">{row.agentVersion}</span>
          <span className="ui-cell-sub ui-text-warn">Follows only its IT setting</span>
        </div>
      ) : <span className="is-num">{row.agentVersion}</span>,
    },
    {
      key: 'circle',
      header: 'Update circle',
      width: '150px',
      cell: (row) => {
        const circle = circleText(row, catalog);
        return circle ? (
          <div className="ui-cell-stack">
            <span>{circle.circle}</span>
            <span className="ui-cell-sub">{circle.why}</span>
          </div>
        ) : <span className="ui-cell-sub">Not reported</span>;
      },
    },
    {
      key: 'report',
      header: 'Last report',
      width: '180px',
      cell: (row) => (
        <div className="ui-cell-stack">
          <span className={isStale(row, now) ? 'ui-text-warn' : undefined} title={dateTime(row.reportedAt)}>{agoText(row.reportedAt, now)}</span>
          <span className="ui-cell-sub">{revisionText(row, saved.revision, now)}</span>
        </div>
      ),
    },
    {
      key: 'differs',
      header: 'Differs from everyone',
      className: 'pcsw-differs',
      cell: (row) => {
        const chips = differenceChips(row, saved, catalog);
        if (chips.length === 0) return <StatusChip tone="ok" icon={Check}>Matches everyone</StatusChip>;
        return <span className="ui-chips">{chips.map((chip) => <DifferenceChipView key={chip.key} chip={chip} />)}</span>;
      },
    },
  ];
  const reportedNone = devices !== null && devices.length === 0;
  return (
    <Stack>
      {notice}
      <Panel
        flush
        title={<span className="ui-title-icon"><Icon icon={Monitor} size="md" />PCs</span>}
        description={devices && devices.length > 0 ? pcsDescription(devices, saved.revision) : 'What each PC reports it is running with'}
        footer={(
          <>
            <Icon icon={Info} size="sm" />
            <span>Each PC reports what it runs with every time it checks in, every few minutes. A PC that is off or asleep catches up at its next check-in. Older Gyro versions follow only their IT setting until they update.</span>
          </>
        )}
      >
        <div className="ui-panel-pad ui-panel-pad--divided">
          <div className="pcsw-toolbar">
            <Input
              className="pcsw-filter"
              prefix={<Icon icon={Search} size="sm" />}
              aria-label="Filter PCs"
              placeholder="Filter by person, PC or Gyro version"
              value={query}
              onChange={(event) => { setQuery(event.target.value); setPage(1); }}
            />
            <span className="pcsw-legend" aria-label="Where a value comes from">
              <span>Where a value comes from:</span>
              <span className="pcsw-legend-item"><StatusChip tone="info" icon={Cloud}>Server</StatusChip>set here</span>
              <span className="pcsw-legend-item"><StatusChip tone="neutral" icon={FileCog}>IT setting</StatusChip>the PC&apos;s Intune setting</span>
              <span className="pcsw-legend-item"><StatusChip tone="neutral" icon={CircleDashed}>Default</StatusChip>built into Gyro</span>
            </span>
          </div>
        </div>
        {devices === null && devicesProblem ? (
          <ErrorState compact title="The PCs' reports could not be read" error={devicesProblem} onRetry={onRetry} />
        ) : (
          <Table
            stacked
            stickyHeader={false}
            className="ui-table--tall"
            label="PCs"
            columns={columns}
            rows={shown.rows}
            rowKey={(row) => `${row.deviceId}|${row.person?.oid ?? ''}|${row.receivedAt}`}
            loading={devices === null}
            empty={reportedNone ? (
              <EmptyState compact icon={Monitor} title="No PC has reported yet">
                A PC reports the switches it runs with once it has the Gyro version that follows them.
              </EmptyState>
            ) : (
              <EmptyState compact icon={Search} title="No PC matches">
                {`Nothing matches “${query.trim()}”. Clear the filter to see every PC.`}
              </EmptyState>
            )}
          />
        )}
        {filtered.length > 0 ? (
          <div className="ui-panel-pad ui-panel-pad--top">
            <div className="ui-pager">
              <span className="ui-cell-sub">{shown.text}</span>
              <span className="ui-actions">
                <Button size="sm" disabled={shown.page <= 1} onClick={() => setPage(shown.page - 1)}>Previous</Button>
                <Button size="sm" disabled={shown.page >= shown.pages} onClick={() => setPage(shown.page + 1)}>Next</Button>
              </span>
            </div>
          </div>
        ) : null}
      </Panel>
    </Stack>
  );
}

/** The page. `canEdit`: an IT admin; an auditor sees everything read-only, with no Save. `me`: who is signed in. */
export function PcSwitches({ canEdit, me }: { canEdit: boolean; me?: { email: string; name: string } }) {
  const place = usePlace();
  const view = viewFrom(place.search);
  const [policy, setPolicy] = useState<DesktopPolicyView | null>(null);
  const [policyProblem, setPolicyProblem] = useState<unknown>(null);
  const [devices, setDevices] = useState<DevicePolicyRow[] | null>(null);
  const [devicesProblem, setDevicesProblem] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState<PolicyEdits>(NO_EDITS);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedRevision, setSavedRevision] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  /** Reads the policy and the PCs' reports. The person's edits stay: after a conflict they lay over what was read. */
  const load = async () => {
    setLoading(true);
    const [read, reports] = await Promise.allSettled([api.desktopPolicy(), api.desktopPolicyDevices()]);
    if (read.status === 'fulfilled') {
      setPolicy(read.value);
      setPolicyProblem(null);
      setSaveError(null);
    } else {
      setPolicyProblem(read.reason);
    }
    if (reports.status === 'fulfilled') {
      setDevices(Array.isArray(reports.value) ? reports.value : []);
      setDevicesProblem(null);
    } else {
      setDevicesProblem(reports.reason);
    }
    setNow(Date.now());
    setLoading(false);
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The "4 min ago" lines move on while the page is open.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  const catalog = useMemo(() => catalogOf(policy), [policy]);
  const panels = useMemo(() => panelsFor(catalog), [catalog]);
  const draft = useMemo(() => (policy ? draftOf(policy, edits) : null), [policy, edits]);
  const changes = useMemo(() => (policy ? changesOf(policy, edits) : []), [policy, edits]);

  const edit = (next: PolicyEdits) => {
    if (!canEdit) return;
    setEdits(next);
    setSavedRevision(null);
    setSaveError((current) => (current === CONFLICT_TEXT ? current : null));
  };

  const save = async () => {
    if (!policy || !canEdit) return;
    setSaving(true);
    setSaveError(null);
    try {
      const stored = await api.saveDesktopPolicy(saveBody(policy, edits));
      setPolicy(stored);
      setEdits(NO_EDITS);
      setSavedRevision(stored.revision);
      setNow(Date.now());
    } catch (error) {
      setSaveError(saveFailureText(error, catalog, draft?.people ?? {}));
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    setEdits(NO_EDITS);
    setSaveError(null);
  };

  const hrefFor = (id: PcSwitchesView) => {
    const next = new URLSearchParams(place.search);
    next.set('view', id);
    return `/pc-switches?${next.toString()}`;
  };
  const tabs = [
    { id: 'everyone', label: 'For everyone', href: hrefFor('everyone') },
    { id: 'exceptions', label: 'Exceptions for people', href: hrefFor('exceptions'), count: draft ? Object.keys(draft.people).length : undefined },
    { id: 'pcs', label: 'PCs', href: hrefFor('pcs'), count: devices?.length },
  ];

  const header = (
    <PageHeader
      meta={<span>The switches every PC with Gyro follows. Set them once here; PCs follow within minutes.</span>}
      actions={<Button icon={RefreshCw} onClick={() => void load()} disabled={loading} aria-label="Refresh" className="is-collapse-phone">Refresh</Button>}
      tabs={tabs}
      activeTab={view}
    />
  );

  const offAnswer = (policyProblem as { status?: unknown } | null)?.status === 404;
  let body: ReactNode;
  if (!policy || !draft) {
    body = offAnswer ? (
      <EmptyState title="PC switches are not switched on">This service does not offer PC switches yet, so every PC follows its IT setting.</EmptyState>
    ) : policyProblem ? (
      <ErrorState title="The PC switches could not be read" error={policyProblem} onRetry={() => void load()} retrying={loading} />
    ) : (
      <Stack>
        <Panel title="PC switches" description="Loading…">
          <div className="ui-form">
            <Skeleton width="40%" />
            <Skeleton width="70%" />
            <Skeleton width="55%" />
          </div>
        </Panel>
      </Stack>
    );
  } else {
    const stale = policyProblem && !offAnswer ? (
      <Callout tone="bad" role="alert" title="The PC switches could not be read again just now">
        What shows is what was read before. Refresh in a minute.
      </Callout>
    ) : null;
    const rowProps = { saved: policy, draft, edits, canEdit, exceptionsHref: hrefFor('exceptions'), onEdit: edit };
    body = (
      <>
        {view === 'everyone' ? (
          <Stack>
            {stale}
            {canEdit ? null : READ_ONLY}
            <Callout tone="info" title="On or Off: every PC follows it. Not set: each PC keeps its IT setting.">
              A saved change reaches each PC at its next check-in, within minutes. A person&apos;s exceptions win over these for that person.
            </Callout>
            {panels.map((panel) => <SwitchPanel key={panel.id} panel={panel} {...rowProps} />)}
            <EarlyCircleSection locked={!canEdit || saving} />
          </Stack>
        ) : view === 'exceptions' ? (
          <ExceptionsView notice={stale} saved={policy} draft={draft} edits={edits} catalog={catalog} panels={panels} canEdit={canEdit} onEdit={edit} />
        ) : (
          <PcsView notice={stale} saved={policy} devices={devices} devicesProblem={devicesProblem} catalog={catalog} now={now} onRetry={() => void load()} />
        )}
        {canEdit ? (
          <SaveBar
            className="pcsw-savebar"
            changes={changes.length}
            onSave={() => void save()}
            onDiscard={discard}
            saving={saving}
            error={saveError ?? undefined}
            meta={<span className="pcsw-savebar-rev">{revisionLine(policy, now, me)}</span>}
            saved={savedRevision !== null ? savedText(savedRevision) : undefined}
          />
        ) : null}
      </>
    );
  }

  return (
    <>
      {header}
      <div className="ui-page">{body}</div>
    </>
  );
}

